import { Eye } from 'lucide-react'
import { useProjectPermissions } from '@/auth/useProjectPermissions'

/**
 * Small "View only" pill shown in a designer toolbar in place of the Save
 * button when the current user lacks write permission on the open project
 * (e.g. the Quality Control role). Purely a UX affordance — the backend is the
 * source of truth and already rejects writes with 403.
 *
 * The tooltip names the user's role(s) so it's clear *why* saving is disabled.
 */
export function ReadOnlyBadge({ className, style }: { className?: string; style?: React.CSSProperties }) {
  const { roles } = useProjectPermissions()
  const roleLabel = roles.length
    ? roles.map((r) => r.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())).join(', ')
    : 'View-only access'
  return (
    <span
      className={className}
      title={`You have view-only access (${roleLabel}) — changes can't be saved`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        whiteSpace: 'nowrap',
        cursor: 'default',
        ...style,
      }}
    >
      <Eye size={14} /> View only
    </span>
  )
}

export default ReadOnlyBadge
